import React, { useEffect, FocusEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setMobileSettingsMenuIsOpen as setMobileSettingsMenuIsOpenAction } from '../../actions/SettingsActions';
import { MENU_CLOSE_DELAY } from '../../constants';
import { TabOption } from '../../models/menuModels';
import { Store } from '../../reducers';
import MaterialIcon from '../MaterialIcon';
import NavigationItem from '../menu/NavigationItem';
import SubMenuMobile from './SubMenuMobile';
import styles from './TabMenuMobile.module.scss';

export type Props = {
    menus: TabOption[];
    getSelectedMenuByPath: (path: string) => TabOption;
};

const TabMenuMobile = ({ getSelectedMenuByPath, menus }: Props): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { pathname, search } = useLocation();
    const dispatch = useDispatch();
    const { mobileMenuIsOpen: mobileMenuOpen } = useSelector((state: Store) => state.userSettings);

    const selectedMenu = getSelectedMenuByPath(pathname);
    const selectedSubMenu = selectedMenu?.subNavigation?.find(element => {
        const elementPath = `${selectedMenu.path}?subPage=${element.queryParam}`;
        const elementPathWithEmptyQuery = element.queryParam.length === 0 ? selectedMenu.path : elementPath;
        return (
            elementPath === pathname ||
            elementPath === `${pathname}${search}` ||
            elementPathWithEmptyQuery === `${pathname}${search}`
        );
    });

    useEffect(() => {
        if (mobileMenuOpen && selectedMenu) {
            const focusedItem = document.getElementById(selectedMenu.text);
            if (focusedItem) {
                focusedItem.focus();
            }
        }
    }, [mobileMenuOpen]);

    const setMobileSettingsMenuIsOpen = (isOpen: boolean): void => {
        dispatch(setMobileSettingsMenuIsOpenAction(isOpen));
    };
    const closeMenu = (): void => {
        setTimeout(() => {
            setMobileSettingsMenuIsOpen(false);
        }, MENU_CLOSE_DELAY);
    };
    const menuId = 'mobile-menu';
    const navItemBlur = (event: FocusEvent<HTMLElement>): void => {
        const enterTarget = event.relatedTarget;
        const settingsMenu: Node | null = document.getElementById(menuId);
        if (!enterTarget || !settingsMenu || !settingsMenu.contains(enterTarget)) {
            closeMenu();
        }
    };
    const openCloseMenu = (): void => {
        setMobileSettingsMenuIsOpen(!mobileMenuOpen);
    };

    return (
        <div id={menuId} className={styles.container}>
            <div
                role="button"
                id="settings-menu"
                className={styles.button}
                tabIndex={0}
                onBlur={navItemBlur}
                onClick={openCloseMenu}
                onKeyUp={(event): void => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    openCloseMenu();
                }}
            >
                <div className={styles.buttonContent}>{txt(selectedSubMenu?.title || selectedMenu.text)}</div>
                <MaterialIcon name="keyboard_arrow_down" />
            </div>
            <div className={styles.rootElement}>
                <ul className={mobileMenuOpen ? styles.openDropdown : styles.dropdown}>
                    {menus.map(menuItem => {
                        if (menuItem.subNavigation) {
                            return (
                                <SubMenuMobile
                                    key={menuItem.text}
                                    tab={menuItem}
                                    subNavigation={menuItem.subNavigation}
                                    closeMenu={closeMenu}
                                />
                            );
                        }
                        return (
                            <NavigationItem
                                onBlur={navItemBlur}
                                key={menuItem.text}
                                path={menuItem.path}
                                onClick={closeMenu}
                                title={menuItem.text}
                                navigationType="large"
                                id={menuItem.id}
                                centered
                            />
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
export default TabMenuMobile;
